import React, { useEffect, useRef, useCallback } from 'react';
import Button from '@strava/ui/Button';
import useGoogle from 'hooks/externalSocialPartners/useGoogle';
import Spinner from '@strava/ui/Spinner';
import { AuthorizationStatus } from 'hooks/externalSocialPartners/constants/auth-status-constants';
import {
  remove,
  uniqBy,
  isEmpty,
  find,
  isNil,
  escape,
  toLower,
  orderBy
} from 'lodash-es';
import createNetworkingClient from 'utils/networking-client';
import * as EmailValidator from 'email-validator';
import clsx from '@strava/ui/clsx';
import { timeout } from 'utils/general-utils';
import StravaAlert from 'components/StravaAlert';
import { Markup } from 'interweave';
import { FOLLOWING_STATUS } from 'constants/follows/following-constants';
import { useSafeState, useSafeDispatch } from '@strava/react-hooks';
import { useInviteFriendsContext } from '../../../../state/InviteFriendsContext';

import styles from './EmailTabContent.scss';
import LinkGoogleAccount from './components/LinkGoogleAccount';

import { t } from '../../../../utils/invite-friends-utils';
import GmailContactList from './components/GmailContactList/GmailContactList';
import {
  extractEmailsFromString,
  processContactsForUI
} from './EmailTabContent.utils';
import {
  ADD_ALERT,
  CLEAR_ALERT,
  CLEAR_EMAILS,
  UPDATE_EMAILS,
  UPDATE_INVITED_EMAILS_LIST
} from '../../../../state/InviteFriends.actions';

const EmailTabContent = () => {
  const { state, dispatch, track } = useInviteFriendsContext();
  const safeDispatch = useSafeDispatch(dispatch);

  const { currentAthlete, googleClientId, mocked, emails, alert } = state;

  const {
    authorizationStatus,
    contacts,
    requestContacts,
    disconnect
  } = useGoogle({
    clientId: googleClientId,
    mocked
  });

  const [processedContacts, setProcessedContacts] = useSafeState([]);
  const [isProcessingContacts, setIsProcessingContacts] = useSafeState(true);
  const [isSendingEmails, setIsSendingEmails] = useSafeState(false);

  const emailInputRef = useRef(null);

  const emailMessage = t('modal.email.message');

  const dispatchAlert = useSafeDispatch(
    useCallback(
      ({ severity, message, target }) => {
        dispatch({
          type: ADD_ALERT,
          payload: {
            alert: {
              severity,
              message,
              target
            }
          }
        });
      },
      [dispatch]
    )
  );

  useEffect(() => {
    if (authorizationStatus === AuthorizationStatus.UNAUTHORIZED) {
      dispatchAlert({
        severity: 'warning',
        message: t('modal.email.unauthorized')
      });
    } else if (authorizationStatus === AuthorizationStatus.DATA_FETCHED) {
      // extract primary entries for each contact and remove entries without email addresses
      let results = processContactsForUI(contacts);
      // eliminate duplicates
      results = uniqBy(results, 'email');
      // remove logged-in athlete from the results
      remove(results, (c) => c.email === currentAthlete.email);

      if (isEmpty(results)) {
        setIsProcessingContacts(false);
      } else {
        const importedEmails = results.map((c) => c.email);

        // find friends who are already on strava and update their entries with strava data
        createNetworkingClient()
          .post('/athletes/email_contacts_on_strava', {
            emails: importedEmails
          })
          .then((response) => {
            if (response && response.status === 200) {
              const { data: friendsAlreadyOnStrava } = response;
              friendsAlreadyOnStrava.forEach((friend) => {
                const contact = find(
                  results,
                  (c) => toLower(c.email) === toLower(friend.email)
                );
                if (!isNil(contact)) {
                  contact.isOnStrava = true;
                  contact.id = friend.id;
                  contact.picture = friend.picture;
                  contact.approveFollowers = friend.approve_followers;
                  contact.followingStatus = friend.following_status;
                  contact.name = friend.display_name;
                  contact.badge = friend.badge;
                }
              });
            } else {
              dispatchAlert({
                severity: 'error',
                message: t(`modal.errors.generic`)
              });
            }
            results = orderBy(
              results,
              [
                (contact) =>
                  [
                    FOLLOWING_STATUS.canFollow,
                    FOLLOWING_STATUS.canRequest
                  ].includes(contact.followingStatus), // 1st  - followable athletes
                (contact) => contact.isOnStrava, // 2nd - all other strava athletes
                (contact) => isNil(contact.name) || contact.name === '', // 3rd - google contacts with display name
                (contact) => contact.name // 4th - the rest, order all groups by display name
              ],
              ['desc', 'desc', 'asc', 'asc']
            );

            setProcessedContacts(results);
          })
          .catch(() => {
            dispatchAlert({
              severity: 'error',
              message: t(`modal.errors.generic`)
            });
          })
          .finally(() => {
            setIsProcessingContacts(false);
          });
      }
    }
  }, [
    authorizationStatus,
    contacts,
    currentAthlete.email,
    dispatchAlert,
    setIsProcessingContacts,
    setProcessedContacts
  ]);

  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [emailInputRef]);

  // show error message to athlete when something goes wrong during authorization
  useEffect(() => {
    if (authorizationStatus === AuthorizationStatus.ERROR) {
      dispatchAlert({ severity: 'error', message: t(`modal.errors.generic`) });
    }
  }, [authorizationStatus, dispatchAlert]);

  const alreadyOnStravaMessage = (alreadyOnStrava) => {
    let message;
    const numOnStrava = alreadyOnStrava.length;

    if (numOnStrava === 1) {
      message = t('modal.email.already_on_strava', {
        email_address: escape(alreadyOnStrava[0])
      });
    } else if (numOnStrava === 2) {
      message = t('modal.email.x_and_y_already', {
        email_address: escape(alreadyOnStrava[0]),
        other_email_address: escape(alreadyOnStrava[1])
      });
    } else {
      message = t('modal.email.x_and_y_and_other_already', {
        email_address: escape(alreadyOnStrava[0]),
        other_email_address: escape(alreadyOnStrava[1]),
        count: numOnStrava - 2
      });
    }
    return message;
  };

  const isValidEmailList = (emailList) => {
    return isNil(emailList.find((email) => !EmailValidator.validate(email)));
  };

  const handleFormSubmission = async (event) => {
    event.preventDefault();

    dispatch({ type: CLEAR_ALERT });
    const emailList = extractEmailsFromString(emails);

    track({
      action: 'click',
      element: 'send_email',
      properties: {
        current_tab: 'email',
        email_count: emailList.length,
        is_valid_email_list: isValidEmailList(emailList),
        emails: emailList.toString()
      }
    });

    // validate emails
    for (let i = 0; i < emailList.length; i++) {
      if (!EmailValidator.validate(emailList[i])) {
        dispatchAlert({
          severity: 'error',
          message: t(`modal.errors.invalid_email`),
          target: 'email-input'
        });
        emailInputRef.current.focus();
        return;
      }
    }

    setIsSendingEmails(true);
    createNetworkingClient()
      .post(`/invites`, {
        to: emailList,
        body: emailMessage,
        invite_dialog: true
      })
      .then(async (response) => {
        if (response.status === 200) {
          const { sent, not_sent: notSent } = response.data;
          if (!isEmpty(sent)) {
            dispatchAlert({
              severity: 'success',
              message: t('modal.email.sent')
            });
            dispatch({
              type: UPDATE_INVITED_EMAILS_LIST,
              payload: sent
            });
            dispatch({ type: CLEAR_EMAILS });

            // `emails sent` message auto-clears after 4 seconds
            await timeout(4000);

            safeDispatch({ type: CLEAR_ALERT });
          }
          if (!isEmpty(notSent)) {
            const { already_on_strava: alreadyOnStrava } = notSent;
            if (!isEmpty(alreadyOnStrava)) {
              dispatchAlert({
                severity: 'warning',
                message: (
                  <Markup
                    noWrap={true}
                    content={alreadyOnStravaMessage(alreadyOnStrava)}
                  />
                )
              });
            }
          }
        } else {
          // TODO: log sentry error
          dispatchAlert({
            severity: 'error',
            message: t('modal.errors.generic')
          });
        }
      })
      .catch(() => {
        // TODO: log sentry error
        dispatchAlert({
          severity: 'error',
          message: t('modal.errors.generic')
        });
      })
      .finally(() => {
        setIsSendingEmails(false);
      });
  };

  const handleInputChange = (event) => {
    dispatch({ type: CLEAR_ALERT });
    dispatch({ type: UPDATE_EMAILS, payload: { emails: event.target.value } });
  };

  return (
    <>
      {!isEmpty(alert) && (
        <StravaAlert severity={alert.severity}>{alert.message}</StravaAlert>
      )}
      <div className={styles.flexGrid}>
        <div className={clsx(styles.col, styles.emailColumn)}>
          <h3 className={styles.headline}>{t('modal.email.subtitle')}</h3>
          <form onSubmit={handleFormSubmission}>
            <label className={styles.label} htmlFor="email-input">
              {t('modal.email.addresses')}
            </label>
            <textarea
              className={clsx(
                styles.emailInput,
                styles.textarea,
                alert.target === 'email-input' ? styles.errorBorder : ''
              )}
              name="email"
              id="email-input"
              onChange={handleInputChange}
              ref={emailInputRef}
              value={state.emails}
            />

            <label className={styles.label} htmlFor="body">
              {t('modal.email.message_label')}
            </label>
            <textarea
              disabled={true}
              name="body"
              id="body"
              className={clsx(styles.emailBody, styles.textarea)}
              value={emailMessage}
            />

            <Button
              type="submit"
              variant="primary"
              className={styles.sendButton}
              disabled={
                isEmpty(state.emails) ||
                isSendingEmails ||
                alert.target === 'email-input'
              }
            >
              {isSendingEmails
                ? t('modal.email.sending')
                : t('modal.email.send')}
            </Button>
          </form>
        </div>
        <div className={styles.col}>
          {[
            AuthorizationStatus.AUTHORIZED,
            AuthorizationStatus.DATA_FETCHED
          ].includes(authorizationStatus) && (
            <GmailContactList
              contacts={processedContacts}
              disconnect={disconnect}
              isLoading={isProcessingContacts}
            />
          )}
          {[
            AuthorizationStatus.READY,
            AuthorizationStatus.ERROR,
            AuthorizationStatus.UNAUTHORIZED
          ].includes(authorizationStatus) && (
            <LinkGoogleAccount requestContacts={requestContacts} />
          )}
          {authorizationStatus === AuthorizationStatus.INITIATED && (
            <div className={styles.spinner}>
              <Spinner />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EmailTabContent;
